/**
 * RideroDriversWidget - Extracted from HubDashboard.jsx
 * Shows live Ridero driver status and availability
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RideroDriversWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, onlineRes] = await Promise.all([
          axios.get(`${API}/ridero/drivers/status`, { withCredentials: true }),
          axios.get(`${API}/ridero/drivers/online`, { withCredentials: true })
        ]);
        setData({
          status: statusRes.data,
          online: onlineRes.data
        });
      } catch (err) {
        console.error('Failed to fetch Ridero drivers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const onlinePercent = data?.status ? Math.round((data.status.online / data.status.total_drivers) * 100) : 0;

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="ridero-drivers-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0f3460]/40 flex items-center justify-center">
            <Truck className="w-5 h-5 text-blue-400" />
          </div>
          <h3 className="font-bold">Ridero Drivers</h3>
        </div>
        <span className="text-xs bg-green-500/20 text-green-400 px-2.5 py-1 rounded-full font-medium">
          {data?.status?.online || 0} Online
        </span>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
          <div className="h-20 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Online Progress Bar */}
          <div className="p-3 rounded-xl bg-secondary/30">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm">Online Status</span>
              <span className="text-sm font-bold">{data?.status?.online || 0} / {data?.status?.total_drivers || 0}</span>
            </div>
            <div className="h-3 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500" 
                style={{ width: `${onlinePercent}%` }} 
              />
            </div>
          </div>
          {/* Driver Status Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-lg font-bold text-green-400">{data?.status?.available || 0}</p>
              <p className="text-xs text-green-400/70">Available</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-lg font-bold text-blue-400">{data?.status?.on_delivery || 0}</p>
              <p className="text-xs text-blue-400/70">On Delivery</p>
            </div>
          </div>
          {/* Online Drivers List */}
          {data?.online?.drivers?.length > 0 && (
            <div className="space-y-1.5 max-h-24 overflow-y-auto">
              {data.online.drivers.slice(0, 3).map((driver, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/20 text-sm">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="flex-1 truncate">{driver.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {driver.current_order ? 'Delivering' : 'Ready'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
